import React, { useMemo } from "react";
import { Container } from "react-bootstrap";
import type { DadosSindicatoDetalhe } from "../../../../../../services";
import type { SecaoDocumento } from "../../../../../../domain/shipping/models";
import { Section } from "../../../V1/Tabs/VisaoGeralTab/VisaoGeralBlocks";
import {
  buildCamposExtrasCadastro,
  formatarDataEnvio,
  mapSecoesParaDocumentos,
  numerarCriteriosDocumentos,
  resolveTituloComprovante,
  type MapSecoesOptions,
} from "../../utils/comprovanteEnvioV2Mappers";
import "../../../V1/Tabs/VisaoGeralTab/VisaoGeralTab.css";
import "../../../V1/ShippingV1.css";
import "../ConsultaEnvioDocumentacaoV2/ConsultaEnvioDocumentacaoV2.css";

export type ComprovanteEnvioDocumentacaoV2CorpoProps = {
  dadosSindicato: DadosSindicatoDetalhe | null;
  secoesObrigatorios: SecaoDocumento[];
  secoesBonus: SecaoDocumento[];
  secoesBonusExtra: SecaoDocumento[];
  enviadoEm: string | null;
  tituloLinha2?: string;
  afterDadosSindicato?: React.ReactNode;
  parecerPorSecaoId?: Record<string, (string | React.ReactElement)[]>;
  paraExportacaoPdf?: boolean;
  headerActions?: React.ReactNode;
  containerRef?: React.Ref<HTMLDivElement>;
  className?: string;
  mapSecoesOptions?: Omit<MapSecoesOptions, "paraExportacaoPdf">;
};

const ComprovanteEnvioDocumentacaoV2Corpo: React.FC<
  ComprovanteEnvioDocumentacaoV2CorpoProps
> = ({
  dadosSindicato,
  secoesObrigatorios,
  secoesBonus,
  secoesBonusExtra,
  enviadoEm,
  tituloLinha2 = "ENVIADA",
  afterDadosSindicato,
  parecerPorSecaoId,
  paraExportacaoPdf = false,
  headerActions,
  containerRef,
  className = "",
  mapSecoesOptions,
}) => {
  const dataFormatada = formatarDataEnvio(enviadoEm);
  const tituloComprovante = resolveTituloComprovante(tituloLinha2);
  const camposExtrasCadastro = useMemo(
    () => buildCamposExtrasCadastro(dadosSindicato),
    [dadosSindicato],
  );

  const secaoMapOptions = useMemo(
    (): MapSecoesOptions => ({
      ...mapSecoesOptions,
      paraExportacaoPdf,
    }),
    [mapSecoesOptions, paraExportacaoPdf],
  );

  const documentosObrigatoriosNum = useMemo(
    () =>
      numerarCriteriosDocumentos(
        mapSecoesParaDocumentos(
          secoesObrigatorios,
          secaoMapOptions,
          parecerPorSecaoId,
        ),
      ),
    [secoesObrigatorios, secaoMapOptions, parecerPorSecaoId],
  );
  const documentosBonusNum = useMemo(
    () =>
      numerarCriteriosDocumentos(
        mapSecoesParaDocumentos(
          secoesBonus,
          secaoMapOptions,
          parecerPorSecaoId,
        ),
      ),
    [secoesBonus, secaoMapOptions, parecerPorSecaoId],
  );
  const documentosBonusExtraNum = useMemo(
    () =>
      numerarCriteriosDocumentos(
        mapSecoesParaDocumentos(
          secoesBonusExtra,
          secaoMapOptions,
          parecerPorSecaoId,
        ),
      ),
    [secoesBonusExtra, secaoMapOptions, parecerPorSecaoId],
  );

  const d = dadosSindicato;

  return (
    <Container
      ref={containerRef}
      className={`shipping-container visao-geral-container consulta-envio-v2-page shipping-doc-v2-font ${className}`.trim()}
    >
      <header className="consulta-envio-page-header">
        <h1 className="visao-geral-page-title">{tituloComprovante}</h1>
        {headerActions ? (
          <div className="consulta-envio-title-right consulta-envio-comprovante-actions">
            {headerActions}
          </div>
        ) : null}
      </header>

      <section className="visao-geral-section consulta-envio-slot-folha">
        <div className="consulta-envio-folha-cartao">
          <h2 className="visao-geral-section-title">CADASTRO</h2>
          <div
            className="consulta-envio-cadastro-grid"
            aria-label="Identificação do sindicato e do envio"
          >
            <div className="consulta-envio-cadastro-grid-col">
              <span className="consulta-envio-rotulo">CNPJ DO SINDICATO</span>
              <span className="consulta-envio-texto">
                {d?.cnpj?.trim() || "Não informado"}
              </span>
            </div>
            <div className="consulta-envio-cadastro-grid-col">
              <span className="consulta-envio-rotulo">NOME DO SINDICATO</span>
              <span className="consulta-envio-texto">
                {d?.nomeSindicato?.trim() || d?.nome?.trim() || "Não informado"}
              </span>
            </div>
            <div className="consulta-envio-cadastro-grid-col">
              <span className="consulta-envio-rotulo">DATA DE ENVIO</span>
              <span className="consulta-envio-texto">{dataFormatada || "—"}</span>
            </div>
          </div>

          {camposExtrasCadastro.length > 0 ? (
            <div
              className="consulta-envio-meta-rodape"
              aria-label="Demais dados do sindicato"
            >
              {camposExtrasCadastro.map(({ label, value }, idx) => (
                <span key={`${label}-${idx}`} className="consulta-envio-meta-item">
                  <span className="consulta-envio-meta-label">{label}:</span>{" "}
                  <span className="consulta-envio-meta-valor">{value}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {afterDadosSindicato}


      {documentosObrigatoriosNum.length > 0 ? (
        <Section
          titulo="DOCUMENTOS OBRIGATÓRIOS"
          documentos={documentosObrigatoriosNum}
          folhaCartao
        />
      ) : null}
      {documentosBonusNum.length > 0 ? (
        <Section titulo="DOCUMENTOS BÔNUS" documentos={documentosBonusNum} folhaCartao />
      ) : null}
      {documentosBonusExtraNum.length > 0 ? (
        <Section
          titulo="DOCUMENTOS BÔNUS EXTRA"
          documentos={documentosBonusExtraNum}
          folhaCartao
        />
      ) : null}
    </Container>
  );
};

export default ComprovanteEnvioDocumentacaoV2Corpo;
